import { useContext, useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { BiX } from "react-icons/bi";
import { BsFilter } from "react-icons/bs";
import Select, { components } from "react-select";
import { RegulationContext } from "../../../../App";
import { CategoryList, DataViewCategoryType, StandardCategory } from "../../../../core/constants/common";
import StandardService from "../../../../core/service/StandardService";
import "./StandardsFilter.scss";

// Same react-select building blocks/skin ("dvSelect") as DataViewFilterNew.js,
// kept local here the same way DataViewFilterNew.js keeps them local.
const MAX_VISIBLE_CHIPS = 2;

const flattenSelectOptions = (options) => {
  return (options ?? []).reduce((acc, item) => {
    if (item?.options) {
      return acc.concat(flattenSelectOptions(item.options));
    }
    acc.push(item);
    return acc;
  }, []);
};

const MultiSelectOption = (props) => {
  const { isSelected, label } = props;
  return (
    <components.Option {...props}>
      <Form.Check
        type="checkbox"
        readOnly
        checked={isSelected}
        label={label}
        className="dvSelect-option-check" />
    </components.Option>
  );
};

const MultiValueChip = (props) => {
  const { index, getValue } = props;
  if (index < MAX_VISIBLE_CHIPS) {
    return <components.MultiValue {...props} />;
  }
  if (index === MAX_VISIBLE_CHIPS) {
    return (
      <div className="dvSelect__multi-value dvSelect__multi-value--overflow">
        <span className="dvSelect__multi-value__label">{props.selectProps.moreLabel(getValue().length - MAX_VISIBLE_CHIPS)}</span>
      </div>
    );
  }
  return null;
};

const MultiSelectMenuList = (props) => {
  const { children, options, selectProps } = props;
  return (
    <components.MenuList {...props}>
      <div className="dvSelect__menu-actions">
        <button type="button"
          className="dvSelect__menu-action"
          onMouseDown={(e) => {
            e.preventDefault();
            const flatOptions = flattenSelectOptions(options);
            const limitedOptions = selectProps.selectionLimit
              ? flatOptions.slice(0, selectProps.selectionLimit)
              : flatOptions;
            selectProps.onChange(limitedOptions, { action: 'select-option' });
          }}>
          {selectProps.selectAllLabel}
        </button>
        <button type="button"
          className="dvSelect__menu-action dvSelect__menu-action--clear"
          onMouseDown={(e) => { e.preventDefault(); selectProps.onChange([], { action: 'clear' }); }}>
          {selectProps.clearAllLabel}
        </button>
      </div>
      {children}
    </components.MenuList>
  );
};

const StandardsFilter = ({ t, standardId, initialFilter, onApplyFilter, onFilterHide, onResetFilter }) => {
  const { glbRegulation } = useContext(RegulationContext);
  const glbRegulationId = standardId || glbRegulation?.id;

  const [filter, setFilter] = useState(
    initialFilter || {
      category: DataViewCategoryType.ENVIRONMENTAL,
      topicIds: [],
      metricIds: [],
      dpIds: [],
    }
  );

  const [topics, setTopics] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [dataPoints, setDataPoints] = useState([]);
  const [categories, setCategories] = useState([]);
  const [displayFilter, setDisplayFilter] = useState(true);

  useEffect(() => {
    if (!glbRegulationId) return;
    fetchCategories();
  }, [glbRegulationId]);

  useEffect(() => {
    if (initialFilter) {
      setFilter(initialFilter);
    } else if (filter.category === null || filter.category === undefined) {
      setFilter({
        ...filter,
        category: DataViewCategoryType.ENVIRONMENTAL,
      });
    }
  }, [initialFilter]);

  useEffect(() => {
    if (!glbRegulationId) return;
    if (filter.category === null || filter.category === undefined) {
      const defaultFilter = {
        ...filter,
        category: DataViewCategoryType.ENVIRONMENTAL,
      };
      setFilter(defaultFilter);
      return;
    }
    fetchTopics();
  }, [glbRegulationId, filter.category]);

  useEffect(() => {
    if (!glbRegulationId || filter.topicIds.length === 0) {
      setMetrics([]);
      setDataPoints([]);
      return;
    }
    fetchMetrics();
  }, [glbRegulationId, filter.topicIds]);

  useEffect(() => {
    if (!glbRegulationId || filter.metricIds.length === 0) {
      setDataPoints([]);
      return;
    }
    fetchDataPoints();
  }, [glbRegulationId, filter.metricIds]);

  const fetchCategories = async () => {
    try {
      const response = await StandardService.getCategory(glbRegulationId);
      if (response.status === 200) {
        setCategories(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchTopics = async () => {
    try {
      const response = await StandardService.getTopicsByStandard(
        glbRegulationId,
        false,
        StandardCategory.QUANTITATIVE,
        filter.category
      );
      if (response.status === 200) {
        setTopics(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching topics:", error);
    }
  };

  const fetchMetrics = async () => {
    try {
      const topicIdsString = filter.topicIds.join(",");
      const response = await StandardService.getMetricsForTopics(
        glbRegulationId,
        topicIdsString,
        filter.category || 0
      );
      if (response.status === 200) {
        setMetrics(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching metrics:", error);
    }
  };

  const fetchDataPoints = async () => {
    try {
      const allDataPoints = [];
      for (const metricId of filter.metricIds) {
        const response = await StandardService.getDataPoints(metricId);
        if (response.status === 200 && response.data?.body?.dataPoints) {
          allDataPoints.push(...response.data.body.dataPoints);
        }
      }
      setDataPoints(allDataPoints);
    } catch (error) {
      console.error("Error fetching data points:", error);
    }
  };

  const onTopicsChange = (selectedItems) => {
    setFilter({
      ...filter,
      topicIds: (selectedItems ?? []).map((item) => item.id),
      metricIds: [],
      dpIds: [],
    });
  };

  const onMetricsChange = (selectedItems) => {
    setFilter({
      ...filter,
      metricIds: (selectedItems ?? []).map((item) => item.id),
      dpIds: [],
    });
  };

  const onDataPointsChange = (selectedItems) => {
    setFilter({
      ...filter,
      dpIds: (selectedItems ?? []).map((item) => item.id),
    });
  };

  const getSelectedTopics = () => {
    return topics.filter((topic) => filter.topicIds.indexOf(topic.id) >= 0);
  };

  const getSelectedMetrics = () => {
    return metrics.filter((metric) => filter.metricIds.indexOf(metric.id) >= 0);
  };

  const getSelectedDataPoints = () => {
    return dataPoints.filter((dp) => filter.dpIds.indexOf(dp.id) >= 0);
  };

  const applyFilter = () => {
    onApplyFilter(filter);
    onFilterHide(false);
  };

  const resetFilter = () => {
    const resetFilterData = {
      category: DataViewCategoryType.ENVIRONMENTAL,
      topicIds: [],
      metricIds: [],
      dpIds: [],
    };
    setFilter(resetFilterData);
    if (onResetFilter) {
      onResetFilter();
    }
  };

  const commonSelectProps = {
    classNamePrefix: "dvSelect",
    unstyled: true,
    isMulti: true,
    placeholder: t("Select.label"),
    noOptionsMessage: () => t("NoData.label"),
    selectAllLabel: t("SelectAll.label"),
    clearAllLabel: t("ClearAll.label"),
    moreLabel: (count) => t("MoreSelected.label", { count }),
    components: { Option: MultiSelectOption, MultiValue: MultiValueChip, MenuList: MultiSelectMenuList },
    hideSelectedOptions: false,
    closeMenuOnSelect: false,
    menuPosition: "fixed",
  };

  return (
    <div>
      {displayFilter && (
        <div className="dv-filter-holder 7">
          <div className="dv-filter-holder_head">
            <h5><BsFilter className="dv-filter-holder_headIcon" />{t("Filter.label")}</h5>
            <div
              className="btnClose"
              onClick={() => onFilterHide(false)}
              aria-label={t("Filter.close.label") || "Close filter"}
            >
              <BiX />
            </div>
          </div>
          <div className="dv-filter-holder_body">
            <div className="row m-0">
              <div className="col-lg-6 p-2">
                <label htmlFor="categoryFilter">{t("Category.label")}</label>
                <select
                  id="categoryFilter"
                  className="form-select"
                  onChange={(e) =>
                    setFilter({
                      ...filter,
                      category: e.target.value ? +e.target.value : null,
                      topicIds: [],
                      metricIds: [],
                      dpIds: [],
                    })
                  }
                  value={filter.category !== null && filter.category !== undefined ? filter.category : DataViewCategoryType.ENVIRONMENTAL}
                >
                  <option value="">{t("Select.label")}</option>
                  {CategoryList({ t }).map((c) => (
                    <option value={c.id} key={c.key}>
                      {c.text}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg-6 p-2">
                <label>{t("Topic.label")}</label>
                <Select
                  {...commonSelectProps}
                  inputId="topicMultiSelect"
                  options={topics}
                  value={getSelectedTopics()}
                  getOptionValue={(o) => o.id}
                  getOptionLabel={(o) => o.name}
                  onChange={onTopicsChange} />
              </div>
              <div className="col-lg-6 p-2">
                <label>{t("Metrics.label")}</label>
                <Select
                  {...commonSelectProps}
                  inputId="metricMultiSelect"
                  options={metrics}
                  value={getSelectedMetrics()}
                  getOptionValue={(o) => o.id}
                  getOptionLabel={(o) => o.name}
                  onChange={onMetricsChange} />
              </div>
              <div className="col-lg-6 p-2">
                <label>{t("DataPoints.label")}</label>
                <Select
                  {...commonSelectProps}
                  inputId="dataPointMultiSelect"
                  options={dataPoints}
                  value={getSelectedDataPoints()}
                  getOptionValue={(o) => o.id}
                  getOptionLabel={(o) => o.name}
                  onChange={onDataPointsChange} />
              </div>
            </div>
          </div>
          <div className="dv-filter-holder_footer">
            <Button variant="outline-secondary" onClick={() => resetFilter()} className="px-4">
              {t("Reset.label")}
            </Button>
            <Button variant="primary" onClick={() => applyFilter()} className="sbmBtn px-4">
              {t("Apply.label")}
            </Button>

          </div>
        </div>
      )}
    </div>
  );
};

export default withTranslation()(StandardsFilter);

